import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './Dialog.module.css';

export type DialogVariant = 'responsive' | 'sheet' | 'fullscreen';

export type DialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog (rendered as-is via asChild). */
  trigger?: ReactNode;
  title: ReactNode;
  /** Visually hide the title (it's still announced). */
  hideTitle?: boolean;
  description?: ReactNode;
  children?: ReactNode;
  /** Sticky footer, typically the actions. */
  footer?: ReactNode;
  /**
   * responsive = bottom sheet on phones, centered dialog from md (default) ·
   * sheet = always a bottom sheet · fullscreen = edge to edge (image viewer, import flow).
   */
  variant?: DialogVariant;
  className?: string;
};

const DISMISS_DISTANCE = 96;

/** Drag the grabber (or header) down to dismiss a sheet, like a native one. */
function useDragToDismiss(onDismiss: () => void) {
  const start = useRef<number | null>(null);
  const [offset, setOffset] = useState(0);
  const onPointerDown = (e: PointerEvent) => {
    start.current = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (start.current !== null) setOffset(Math.max(0, e.clientY - start.current));
  };
  const onPointerUp = () => {
    if (offset > DISMISS_DISTANCE) onDismiss();
    start.current = null;
    setOffset(0);
  };
  return {
    offset,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}

export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  hideTitle,
  description,
  children,
  footer,
  variant = 'responsive',
  className,
}: DialogProps) {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const setOpen = (v: boolean) => {
    setInnerOpen(v);
    onOpenChange?.(v);
  };
  const drag = useDragToDismiss(() => setOpen(false));

  return (
    <RadixDialog.Root open={isOpen} onOpenChange={setOpen}>
      {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={s.overlay} />
        <RadixDialog.Content
          className={cx(s.content, className)}
          data-variant={variant}
          onOpenAutoFocus={(e) => {
            // Focus the dialog itself (announced by screen readers) rather than the first
            // control, so opening a sheet doesn't flash a focus ring on its close button.
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
          style={drag.offset ? ({ '--drag': `${drag.offset}px` } as CSSProperties) : undefined}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          {variant !== 'fullscreen' ? (
            <span className={s.grabber} aria-hidden="true" {...drag.handlers} />
          ) : null}
          <header className={s.header} data-hidden-title={hideTitle || undefined}>
            <RadixDialog.Title className={s.title}>{title}</RadixDialog.Title>
            <RadixDialog.Close className={s.close} aria-label="Close">
              <Icon icon={X} size="md" />
            </RadixDialog.Close>
          </header>
          {description ? (
            <RadixDialog.Description className={s.description}>
              {description}
            </RadixDialog.Description>
          ) : null}
          {children ? <div className={s.body}>{children}</div> : null}
          {footer ? <footer className={s.footer}>{footer}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Always a bottom sheet (action lists, quick forms). */
export function Sheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />;
}

export const DialogClose = RadixDialog.Close;
